/* ==========================================================================
   Buy Together (Compre Junto)
   Adds the selected bundle products to the cart via AJAX, then opens the
   side cart drawer and plays the fly-to-cart animation — mirroring the
   theme's native product-form.js (#processBatchAddToCart / #animateFlyToCart).
   Falls back to the /cart page when the drawer cart is not available.
   ========================================================================== */

import { CartLinesUpdateEvent } from '@shopify/events';
import '@theme/fly-to-cart';

if (!customElements.get('buy-together')) {
  class RkBuyTogether extends HTMLElement {
    connectedCallback() {
      this.ctaButton = this.querySelector('[data-buy-together-cta]');
      this.errorMsg = this.querySelector('[data-error-msg]');
      this.cards = Array.from(this.querySelectorAll('.rk-buy-together__card'));

      this.bindEvents();
    }

    bindEvents() {
      // Checkbox selection logic
      this.cards.forEach((card) => {
        const checkbox = card.querySelector('.rk-buy-together__checkbox:not(.rk-buy-together__checkbox--locked)');
        if (checkbox) {
          checkbox.addEventListener('click', () => {
            const isPressed = checkbox.getAttribute('aria-pressed') === 'true';
            checkbox.setAttribute('aria-pressed', String(!isPressed));
            checkbox.classList.toggle('rk-buy-together__checkbox--active', !isPressed);
            card.classList.toggle('rk-buy-together__card--selected', !isPressed);
          });
        }
      });

      // Add to cart
      if (this.ctaButton) {
        this.ctaButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.addToCart();
        });
      }
    }

    /**
     * Collects the selected cards, resolving each to a purchasable variant.
     * Stops at the first specific problem (missing option, unavailable combo,
     * out of stock) and returns a human-readable message describing it.
     * @returns {{ items: Array<{id: number, quantity: number}>, sources: Array<{el: Element, image: string}>, errorMessage: string|null }}
     */
    collectSelection() {
      const items = [];
      const sources = [];
      let errorMessage = null;

      for (const card of this.cards) {
        const checkbox = card.querySelector('.rk-buy-together__checkbox');
        if (!checkbox || checkbox.getAttribute('aria-pressed') !== 'true') continue;

        const jsonScript = card.querySelector('.rk-buy-together__product-json');
        if (!jsonScript) continue;

        let productData;
        try {
          productData = JSON.parse(jsonScript.textContent);
        } catch {
          continue;
        }

        const title =
          productData.title ||
          card.querySelector('.rk-buy-together__product-title')?.textContent.trim() ||
          'este produto';
        const selects = Array.from(card.querySelectorAll('select'));
        let variant = null;

        if (selects.length > 0) {
          const selectedOptions = { option1: null, option2: null, option3: null };
          let missingSelect = null;

          selects.forEach((select) => {
            if (!select.value && !missingSelect) missingSelect = select;
            const optIndex = parseInt(select.dataset.optionIndex, 10) + 1;
            selectedOptions['option' + optIndex] = select.value;
          });

          if (missingSelect) {
            if (!errorMessage) {
              const optName = (missingSelect.getAttribute('aria-label') || 'a variação').trim();
              errorMessage = `Selecione ${optName} de "${title}".`;
            }
            continue;
          }

          variant = productData.variants.find(
            (v) =>
              (!selectedOptions.option1 || v.option1 === selectedOptions.option1) &&
              (!selectedOptions.option2 || v.option2 === selectedOptions.option2) &&
              (!selectedOptions.option3 || v.option3 === selectedOptions.option3)
          );

          if (!variant) {
            if (!errorMessage) errorMessage = `A combinação selecionada de "${title}" não está disponível.`;
            continue;
          }
        } else if (productData.variants && productData.variants.length > 0) {
          variant = productData.variants[0];
        }

        if (!variant) continue;

        // Out of stock — but respect "continue selling when out of stock" (oversell).
        if (variant.available === false && variant.inventory_policy !== 'continue') {
          if (!errorMessage) errorMessage = `"${title}" está esgotado.`;
          continue;
        }

        items.push({ id: variant.id, quantity: 1 });

        const imageEl = card.querySelector('.rk-buy-together__image');
        const image = imageEl?.currentSrc || imageEl?.src;
        if (imageEl && image) sources.push({ el: imageEl, image });
      }

      return { items, sources, errorMessage };
    }

    /**
     * Shows a specific error message below the CTA.
     * @param {string} message
     */
    showError(message) {
      if (!this.errorMsg) return;
      this.errorMsg.textContent = message;
      this.errorMsg.style.display = 'block';
    }

    clearError() {
      if (this.errorMsg) this.errorMsg.style.display = 'none';
    }

    /**
     * Extracts a friendly message from a Shopify cart error response.
     * The cart/add.js endpoint returns { status, message, description } — the
     * `description` is already localized and human-readable (e.g. sold out,
     * quantity limits), so prefer it.
     * @param {any} response
     * @returns {string}
     */
    serverErrorMessage(response) {
      const desc = response && (response.description || response.message);
      return desc || 'Não foi possível adicionar ao carrinho. Tente novamente.';
    }

    async addToCart() {
      this.clearError();

      const { items, sources, errorMessage } = this.collectSelection();

      if (errorMessage) {
        this.showError(errorMessage);
        return;
      }

      if (items.length === 0) {
        this.showError('Selecione ao menos um produto disponível.');
        return;
      }

      // Side cart drawer is only present when the store uses cart_type == 'drawer'
      // and we're not on the cart page. Otherwise fall back to the cart page.
      const hasDrawer = Boolean(document.querySelector('cart-drawer-component'));

      const originalText = this.ctaButton.textContent;
      this.ctaButton.textContent = 'Adicionando...';
      this.ctaButton.disabled = true;

      // Play the fly-to-cart animation up front (as the native product form does),
      // regardless of the AJAX round-trip.
      if (hasDrawer) sources.forEach(({ el, image }) => this.flyToCart(el, image));

      try {
        if (hasDrawer) {
          await this.addWithDrawer(items);
        } else {
          await this.addAndRedirect(items);
          return; // navigation in progress
        }
        this.ctaButton.textContent = originalText;
        this.ctaButton.disabled = false;
      } catch (error) {
        console.error('[buy-together]', error);
        this.ctaButton.textContent = originalText;
        this.ctaButton.disabled = false;
        this.showError(error?.message || 'Não foi possível adicionar ao carrinho. Tente novamente.');
      }
    }

    /**
     * Adds items and drives the native side cart drawer via CartLinesUpdateEvent.
     * @param {Array<{id: number, quantity: number}>} items
     */
    async addWithDrawer(items) {
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
        .filter(Boolean);

      const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
      const deferred = CartLinesUpdateEvent.createPromise();

      // Announce the pending update so the cart drawer auto-opens.
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
          promise: deferred.promise,
        })
      );

      try {
        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: sectionIds.join(',') }),
        }).then((res) => res.json());

        if (response.status) {
          deferred.resolve({ detail: { didError: true } });
          throw new Error(this.serverErrorMessage(response));
        }

        const cart = await this.refreshCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'buy-together',
            itemCount: totalQuantity,
            sections: response.sections,
            didError: false,
          },
        });
      } catch (error) {
        deferred.reject(error);
        throw error;
      }
    }

    /**
     * Legacy path for stores without a side cart drawer.
     * @param {Array<{id: number, quantity: number}>} items
     */
    async addAndRedirect(items) {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) {
        let message = 'Não foi possível adicionar ao carrinho. Tente novamente.';
        try {
          message = this.serverErrorMessage(await response.json());
        } catch {
          /* keep fallback message */
        }
        throw new Error(message);
      }
      window.location.href = (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';
    }

    /**
     * Re-renders the cart drawer content and returns the updated cart JSON.
     * @returns {Promise<any>}
     */
    async refreshCart() {
      const cartItemsComponent = document.querySelector('cart-items-component');
      if (cartItemsComponent) {
        await customElements.whenDefined('cart-items-component');
        return cartItemsComponent.fetchCartData();
      }
      return fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch cart: ${res.status}`);
        return res.json();
      });
    }

    /**
     * Spawns a <fly-to-cart> element animating an image toward the header cart icon.
     * @param {Element} sourceEl
     * @param {string} imageUrl
     */
    flyToCart(sourceEl, imageUrl) {
      const cartIcon = document.querySelector('.header-actions__cart-icon');
      if (!cartIcon || !sourceEl || !imageUrl) return;

      const fly = /** @type {any} */ (document.createElement('fly-to-cart'));
      fly.classList.add('fly-to-cart--main');
      fly.style.setProperty('background-image', `url(${imageUrl})`);
      fly.style.setProperty('--start-opacity', '0');
      fly.source = sourceEl;
      fly.destination = cartIcon;
      document.body.appendChild(fly);
    }
  }

  customElements.define('buy-together', RkBuyTogether);
}
